import type { PaletteMode } from '@mui/material';
import type { PaletteOptions } from '@mui/material/styles';

/**
 * A color's `main` value plus its lighter and darker variants.
 *
 * `main` carries the rebranded value for every group. The variants mostly do
 * not: only `primary.dark` was re-picked for the rebrand (it is the link and
 * heading text color), and no other `light` or `dark` is referenced anywhere in
 * `src/`. The rest still hold their pre-rebrand values, so they will not
 * necessarily read as the same ramp as the new `main` — `contracting` is the
 * clearest case, with a slate-blue `main` beside purple variants. Get a value
 * from design before reaching for one rather than assuming the ramp is intact.
 */
type ColorVariants = {
  main: string;
  light: string;
  dark: string;
};

/**
 * Every color value that differs between light and dark mode. One set is
 * resolved per theme, which keeps mode branching out of the theme definition
 * itself.
 */
export type ThemeColors = {
  mode: PaletteMode;
  primary: ColorVariants;
  secondary: ColorVariants;
  danger: ColorVariants;
  existingInterest: ColorVariants;
  contracting: ColorVariants;
  executed: ColorVariants;
  background: {
    default: string;
    paper: string;
    elevated: string;
  };
  text: {
    primary: string;
    secondary: string;
    disabled: string;
  };
  ghostWhite: string;
  productType: {
    vppa: string;
    ppa: string;
    rec: string;
  };
  bid: string;
  offer: string;
  input: {
    background: {
      main: string;
      focused: string;
    };
    border: {
      main: string;
      light: string;
    };
    placeholder: string;
  };
  /** Border color used by surfaces: app bar, paper, data grid. */
  border: string;
  /** Dark mode sits the app bar on the page background, light mode on paper. */
  appBarBackground: string;
  /** Hover/selected backgrounds for list items. */
  action: {
    hover: string;
    selected: string;
  };
  menu: {
    background: string;
    borderColor: string;
  };
  menuItemHoverText: string;
  dialogBackdrop: string;
  scrollbarThumb: string;
  autofill: {
    background: string;
    border: string;
  };
  /**
   * Palette keys that are only overridden in one mode. The other mode keeps the
   * MUI defaults, so this must stay a partial palette rather than a full set.
   */
  paletteOverrides: Pick<PaletteOptions, 'divider' | 'action'>;
};

const LIGHT_BORDER = '#e0e0e0';
const LIGHT_GHOST_WHITE = 'rgba(0, 0, 0, 0.4)';

const LIGHT_COLORS: ThemeColors = {
  mode: 'light',
  primary: {
    main: '#59997D',
    light: '#22C55E',
    dark: '#15803D',
  },
  secondary: {
    main: '#707074',
    light: '#A1A1AA',
    dark: '#52525B',
  },
  danger: {
    main: '#FF5B40',
    light: '#EF4444',
    dark: '#B91C1C',
  },
  existingInterest: {
    main: '#FF8A61',
    light: '#FDBA74',
    dark: '#C2410C',
  },
  contracting: {
    main: '#466884',
    light: '#D8B4FE',
    dark: '#7E22CE',
  },
  executed: {
    main: '#33ADFF',
    light: '#7DD3FC',
    dark: '#0369A1',
  },
  background: {
    default: '#E4E4E7',
    paper: '#FFFFFF',
    elevated: '#f5f4f4',
  },
  text: {
    primary: '#181818',
    secondary: '#71717A',
    disabled: '#00000061',
  },
  ghostWhite: LIGHT_GHOST_WHITE,
  productType: {
    vppa: '#6598BA',
    ppa: '#00DBFF',
    rec: '#FF8A61',
  },
  bid: '#2563eb',
  offer: '#dc2626',
  input: {
    background: {
      main: 'rgba(0,0,0, 0.03)',
      focused: 'rgba(0,0,0, 0.05)',
    },
    border: {
      main: 'rgba(0, 0, 0, 0.1)',
      light: 'rgba(0, 0, 0, 0.2)',
    },
    placeholder: 'rgba(0,0,0, 0.1)',
  },
  border: LIGHT_BORDER,
  appBarBackground: '#FFFFFF',
  action: {
    hover: '#f5f5f5',
    selected: 'rgba(0, 0, 0, 0.08)',
  },
  menu: {
    background: 'rgba(255, 255, 255, 0.92)',
    borderColor: LIGHT_BORDER,
  },
  menuItemHoverText: '#000',
  dialogBackdrop: 'rgba(0,0,0,0.8)',
  scrollbarThumb: 'rgba(0, 0, 0, 0.3)',
  autofill: {
    background: '#e3f2fd', // Light blue
    border: '#1976d2', // Blue border
  },
  // Light mode relies on the MUI defaults for divider and action.
  paletteOverrides: {},
};

const DARK_ACTION = '#4a4a4a';
const DARK_DIVIDER = 'rgba(255, 255, 255, 0.2)';
const DARK_GHOST_WHITE = 'rgba(255, 255, 255, 0.4)';

const DARK_COLORS: ThemeColors = {
  mode: 'dark',
  primary: {
    main: '#90FFD4',
    light: '#86EFAC',
    dark: '#33FFB1',
  },
  secondary: {
    main: '#9B9CA2',
    light: '#71717A',
    dark: '#3F3F46',
  },
  danger: {
    main: '#FF5B40',
    light: '#FCA5A5',
    dark: '#EF4444',
  },
  existingInterest: {
    main: '#FF8A61',
    light: '#FDBA74',
    dark: '#EA580C',
  },
  contracting: {
    main: '#6598BA',
    light: '#D8B4FE',
    dark: '#9333EA',
  },
  executed: {
    main: '#00DBFF',
    light: '#7DD3FC',
    dark: '#0284C7',
  },
  background: {
    default: '#050505',
    paper: '#141414',
    elevated: '#3a3a3a',
  },
  text: {
    primary: '#C5C5C5',
    secondary: '#717173',
    disabled: '#FFFFFF33',
  },
  ghostWhite: DARK_GHOST_WHITE,
  productType: {
    vppa: '#6598BA',
    ppa: '#00DBFF',
    rec: '#FF8A61',
  },
  bid: '#7eaaff',
  offer: '#f9644d',
  input: {
    background: {
      main: 'rgba(255,255,255, 0.03)',
      focused: 'rgba(255,255,255, 0.05)',
    },
    border: {
      main: 'rgba(255, 255, 255, 0.1)',
      light: 'rgba(255, 255, 255, 0.2)',
    },
    placeholder: 'rgba(255, 255, 255, 0.1)',
  },
  border: '#333333',
  appBarBackground: '#050505',
  action: {
    hover: DARK_ACTION,
    selected: DARK_ACTION,
  },
  menu: {
    background: 'rgba(10, 10, 10, 0.95)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  menuItemHoverText: '#FFF',
  dialogBackdrop: 'rgba(0,0,0,0.6)',
  scrollbarThumb: '#6b6a6a',
  autofill: {
    background: '#326032', // Light green
    border: '#4caf50', // Green border
  },
  paletteOverrides: {
    divider: DARK_DIVIDER,
    action: {
      hover: DARK_ACTION,
      selected: DARK_ACTION,
    },
  },
};

export const resolveColors = (darkMode: boolean): ThemeColors =>
  darkMode ? DARK_COLORS : LIGHT_COLORS;
